
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Users,
  BarChart3,
  MessageSquare,
  Home,
} from "lucide-react";
import { useDeviceType } from "@/hooks/useDeviceType";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { isPwaMode } from "@/utils/pwaUtils"; // Add this import
import { shouldUsePwaTokenAuth } from "@/utils/auth/tokenAuthUtils"; // Add this import
import { MobileSidebar } from "@/components/mobile/MobileSidebar";
import { BottomNavigation } from "@/components/mobile/BottomNavigation";
import { PatientAddButton } from "@/components/mobile/PatientAddButton";
import { logger } from "@/utils/logger";

export function MobileNavigation() {
  const deviceType = useDeviceType();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Initialize to false
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const handlePwaForceLogout = () => {
      if (isMounted) {
        logger.info("MobileNavigation: pwa-force-logout event received, setting isAuthenticated to false.");
        setIsAuthenticated(false);
      }
    };

    const checkAuthState = async () => {
      if (!isMounted) return;

      const pwaRunning = isPwaMode();
      if (pwaRunning) {
        const hasAuth = await shouldUsePwaTokenAuth();
        if (!hasAuth) {
          if (isMounted) setIsAuthenticated(false);
          return;
        }
        // If PWA auth flag exists, check Supabase session.
        // This is important because token-auth-exchange might have set up a session.
        const { data: sessionData } = await supabase.auth.getSession();
        if (isMounted) setIsAuthenticated(!!sessionData.session && hasAuth); // Must have both PWA auth and Supabase session
      } else {
        // Standard web mode
        const { data: sessionData } = await supabase.auth.getSession();
        if (isMounted) setIsAuthenticated(!!sessionData.session);
      }
    };

    checkAuthState();
    window.addEventListener('pwa-force-logout', handlePwaForceLogout);

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      const pwaRunning = isPwaMode();
      logger.info(`MobileNavigation: onAuthStateChange event: ${event}, session: ${!!session}, pwa: ${pwaRunning}`);

      if (pwaRunning) {
        if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
          setIsAuthenticated(false);
        } else {
          // For other events (like INITIAL_SESSION, SIGNED_IN),
          // re-verify PWA auth flag AND Supabase session.
          const hasAuth = await shouldUsePwaTokenAuth();
          setIsAuthenticated(hasAuth && !!session);
        }
      } else {
        setIsAuthenticated(!!session);
      }
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
      window.removeEventListener('pwa-force-logout', handlePwaForceLogout);
    };
  }, []); // Empty dependency array, runs once on mount

  const shouldShow = (deviceType === 'mobile' || isMobile) && isAuthenticated;

  if (!shouldShow) {
    return null;
  }

  const navItems = [
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/patients", icon: Users, label: "Patients" },
    { path: "/forums", icon: MessageSquare, label: "Forums" },
    { path: "/reports", icon: BarChart3, label: "Reports" },
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/') {
      return true;
    }
    return location.pathname.startsWith(path);
  };

  // Check if current page is patients page
  const isPatientsPage = location.pathname.startsWith('/patients');

  return (
    <>
      <MobileSidebar
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        navItems={navItems}
        isActive={isActive}
      />

      <BottomNavigation
        navItems={navItems}
        isActive={isActive}
        onMenuClick={() => setIsOpen(true)}
      />

      <PatientAddButton visible={isPatientsPage} />

      {/* Adjust bottom padding to account for navigation + safe area + PWA transform */}
      {!isPwaMode() && (
        <div className="pb-[calc(6.8rem+env(safe-area-inset-bottom))]" />
      )}
    </>
  );
}

export default MobileNavigation;
